import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, Search, FileCheck, Award, ArrowRight, TrendingUp, CheckCircle2, Users, Globe, Clock, Star, Zap, Lock, FileText, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { testimonials, processSteps, images, countryCodes } from '../mock';
import PricingSection from '../components/PricingSection';
import { Button } from '../components/ui/button';
import AnimatedBackground from '../components/AnimatedBackground';
import { fetchGeolocation, submitLead, getDialCodeFromCountry } from '../services/apiService';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const HomePage = ({ onOpenPopup }) => {
  const navigate = useNavigate();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [geoData, setGeoData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentProcess, setCurrentProcess] = useState(0);

  useEffect(() => {
    const testimonialInterval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    const processInterval = setInterval(() => {
      setCurrentProcess((prev) => (prev + 1) % processSteps.length);
    }, 4000);

    // Fetch geolocation on mount
    const loadGeolocation = async () => {
      const result = await fetchGeolocation();
      if (result.success) {
        setGeoData(result.data);
      }
    };
    loadGeolocation();

    return () => {
      clearInterval(testimonialInterval);
      clearInterval(processInterval);
    };
  }, []);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.target);
    const name = formData.get('name');
    const email = formData.get('email');
    const countryCode = formData.get('countryCode');
    const phoneNumber = formData.get('phoneNumber');
    const message = formData.get('message');

    // Find country from countryCodes
    const selectedCountry = countryCodes.find(c => c.code === countryCode);
    const dialCode = selectedCountry ? parseInt(selectedCountry.code.replace('+', '')) : 1;
    const country = selectedCountry ? selectedCountry.country.split(' ')[0].substring(0, 2).toUpperCase() : 'US';

    const leadData = {
      name,
      email,
      countryDialCode: dialCode,
      country,
      phoneNumber,
      ipAddress: geoData?.ipAddress || '',
      description: message || '',
      city: geoData?.cityName || '',
      state: geoData?.regionName || '',
      geoLocation: geoData ? {
        isSuccess: true,
        data: geoData,
        errorMessage: '',
        statusCode: 200
      } : null
    };

    const result = await submitLead(leadData);
    setIsSubmitting(false);

    if (result.success) {
      toast.success(result.message || 'Thank you! We will contact you shortly.');
      e.target.reset();
      // Redirect to application page
      // navigate(`/application/main?customerid=${result.customerId}`);
      window.location = `/application/main?customerid=${result.customerId}`
    } else {
      toast.error(result.error || 'Failed to submit. Please try again.');
    }
  };

  return (
    <>
      <Helmet>
        <title>Professional Trademark Registration Services | Trusted Trademark Filer</title>
        <meta name="description" content="Protect your brand with expert trademark registration services. 95% approval rate, 24-48hr filing time, and dedicated support. Get started today with Trusted Trademark Filer." />
      </Helmet>
      <div className="min-h-screen">
      {/* Hero Section - Professional Design with Building Background */}
      <section className="relative py-16 overflow-hidden">
        {/* Background Image - Modern Office Building */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1600')`,
          }}
        ></div>
        
        {/* Overlay with gradient - lighter to show building */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#E2F8FE]/90 via-white/85 to-[#E2F8FE]/90"></div>
        
        {/* Animated Icon Background */}
        <div className="relative z-0">
          <AnimatedBackground />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <div className="space-y-6">
              <div className="inline-block">
                <span className="bg-white text-[#FF4A2A] px-4 py-2 rounded-full text-sm font-semibold border-2 border-[#FF4A2A]">
                  Trusted by 500+ Businesses
                </span>
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                Protect Your Brand with Professional Trademark Services
              </h1>
              <p className="text-lg text-gray-700 leading-relaxed">
                Secure your brand's future with our expert trademark registration services. Fast processing, 95% approval rate, and dedicated support every step of the way.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button
                  onClick={onOpenPopup}
                  className="bg-[#FF4A2A] hover:bg-[#E63E1F] text-white px-8 py-4 rounded-lg font-semibold text-base transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  Get Started Now
                </button>
                <button
                  onClick={() => window.openChatWidget && window.openChatWidget()}
                  className="border-2 border-[#FF4A2A] text-[#FF4A2A] hover:bg-[#FF4A2A] hover:text-white px-8 py-4 rounded-lg font-semibold text-base transition-all duration-300"
                >
                  Chat Now
                </button>
              </div>
              <div className="grid grid-cols-3 gap-6 pt-6">
                <div>
                  <div className="text-xl font-bold text-[#FF4A2A]">95%</div>
                  <div className="text-sm text-gray-600">Approval Rate</div>
                </div>
                <div>
                  <div className="text-xl font-bold text-[#FF4A2A]">24-48hr</div>
                  <div className="text-sm text-gray-600">Filing Time</div>
                </div>
                <div>
                  <div className="text-xl font-bold text-[#FF4A2A]">500+</div>
                  <div className="text-sm text-gray-600">Trademarks Filed</div>
                </div>
              </div>
            </div>

            {/* Right Side - Form */}
            <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                Start Your Trademark Registration
              </h2>
              {geoData && (
                <div className="mb-4 p-3 bg-[#E2F8FE] rounded-lg border border-[#FF4A2A]/20">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <span className="font-semibold">📍 Detected Location:</span>
                    <span>{geoData.countryName}</span>
                  </div>
                </div>
              )}
              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div>
                  <label htmlFor="hero-name" className="block text-base font-medium text-gray-700 mb-2">
                    Enter Your Name *
                  </label>
                  <input
                    id="hero-name"
                    name="name"
                    type="text"
                    required
                    placeholder="Enter your full name"
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF4A2A] focus:border-transparent"
                  />
                </div>

                <div>
                  <label htmlFor="hero-email" className="block text-base font-medium text-gray-700 mb-2">
                    Enter Your Email *
                  </label>
                  <input
                    id="hero-email"
                    name="email"
                    type="email"
                    required
                    placeholder="Enter your email address"
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF4A2A] focus:border-transparent"
                  />
                </div>

                <div>
                  <label htmlFor="hero-phone" className="block text-base font-medium text-gray-700 mb-2">
                    Number *
                  </label>
                  <div className="flex gap-2">
                    <select name="countryCode" className="px-3 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF4A2A] w-40">
                      {countryCodes.map((country, index) => (
                        <option key={index} value={country.code}>
                          {country.code} ({country.country})
                        </option>
                      ))}
                    </select>
                    <input
                      id="hero-phone"
                      name="phoneNumber"
                      type="text"
                      required
                      placeholder="Enter Your Number"
                      maxLength="10"
                      pattern="\d{10}"
                      onInput={(e) => e.target.value = e.target.value.replace(/\D/g, '')}
                      className="flex-1 px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF4A2A] focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="hero-message" className="block text-base font-medium text-gray-700 mb-2">
                    What are you looking for?
                  </label>
                  <textarea
                    id="hero-message"
                    name="message"
                    rows="3"
                    placeholder="e.g., name, logo, slogan"
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF4A2A] focus:border-transparent"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#FF4A2A] hover:bg-[#E63E1F] disabled:bg-gray-400 text-white px-8 py-4 text-base font-semibold rounded-lg transition-colors duration-300 shadow-lg"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Trademark Protection */}
      <section className="py-20 bg-gradient-to-b from-white to-[#E2F8FE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Side - Image */}
            <div className="relative">
              <div className="absolute -top-8 -left-8 w-64 h-64 bg-[#FF4A2A] rounded-full opacity-10 blur-3xl"></div>
              <img
                src={images.about}
                alt="Trademark Protection"
                className="rounded-3xl shadow-2xl w-full h-[450px] object-cover relative z-10 border-4 border-white"
              />
            </div>

            {/* Right Side - Content */}
            <div className="space-y-4">
              <div className="inline-block bg-[#E2F8FE] rounded-full px-6 py-2">
                <span className="text-[#FF4A2A] font-semibold text-sm">TRADEMARK PROTECTION</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900">
                Understanding Trademark Protection
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                A trademark is your business's unique identifier that sets you apart from competitors. Learn why protecting it is crucial for your brand's success.
              </p>

              <div className="space-y-3 pt-2">
                {/* Point 1 */}
                <div className="flex items-start gap-3">
                  <div className="bg-[#FF4A2A] p-2 rounded-lg flex-shrink-0">
                    <Shield className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-base">Exclusive Nationwide Rights</h3>
                    <p className="text-base text-gray-600">Federal registration grants exclusive rights to use your mark across all 50 states.</p>
                  </div>
                </div>

                {/* Point 2 */}
                <div className="flex items-start gap-3">
                  <div className="bg-[#FF4A2A] p-2 rounded-lg flex-shrink-0">
                    <Lock className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-base">Legal Protection</h3>
                    <p className="text-base text-gray-600">Gain legal presumption of ownership and the right to sue infringers in federal court.</p>
                  </div>
                </div>

                {/* Point 3 */}
                <div className="flex items-start gap-3">
                  <div className="bg-[#FF4A2A] p-2 rounded-lg flex-shrink-0">
                    <TrendingUp className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-base">Valuable Business Asset</h3>
                    <p className="text-base text-gray-600">Trademarks can be licensed, sold, or used as collateral to increase your company's value.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive intellectual property and business formation services to protect and grow your brand
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Link to="/services/register-trademark" className="group">
              <div className="bg-gradient-to-br from-[#E2F8FE] to-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 h-full">
                <div className="bg-white w-16 h-16 rounded-xl flex items-center justify-center mb-6 shadow-md group-hover:scale-110 transition-transform duration-300">
                  <Shield className="h-8 w-8 text-[#FF4A2A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Trademark Registration
                </h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  Secure your brand with comprehensive federal trademark registration services
                </p>
              </div>
            </Link>

            <Link to="/services/register-copyright" className="group">
              <div className="bg-gradient-to-br from-[#E2F8FE] to-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 h-full">
                <div className="bg-white w-16 h-16 rounded-xl flex items-center justify-center mb-6 shadow-md group-hover:scale-110 transition-transform duration-300">
                  <FileCheck className="h-8 w-8 text-[#FF4A2A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Copyright Registration
                </h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  Protect your creative works with official copyright registration
                </p>
              </div>
            </Link>

            <Link to="/services/llc-formation" className="group">
              <div className="bg-gradient-to-br from-[#E2F8FE] to-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 h-full">
                <div className="bg-white w-16 h-16 rounded-xl flex items-center justify-center mb-6 shadow-md group-hover:scale-110 transition-transform duration-300">
                  <Building2 className="h-8 w-8 text-[#FF4A2A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Company Formation
                </h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  Form your LLC, corporation, or non-profit with professional assistance
                </p>
              </div>
            </Link>

            <Link to="/services/clearance-search" className="group">
              <div className="bg-gradient-to-br from-[#E2F8FE] to-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 h-full">
                <div className="bg-white w-16 h-16 rounded-xl flex items-center justify-center mb-6 shadow-md group-hover:scale-110 transition-transform duration-300">
                  <Search className="h-8 w-8 text-[#FF4A2A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Trademark Search
                </h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  Comprehensive trademark searches to ensure availability before filing
                </p>
              </div>
            </Link>

            <Link to="/services/amazon-brand-registry" className="group">
              <div className="bg-gradient-to-br from-[#E2F8FE] to-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 h-full">
                <div className="bg-white w-16 h-16 rounded-xl flex items-center justify-center mb-6 shadow-md group-hover:scale-110 transition-transform duration-300">
                  <Award className="h-8 w-8 text-[#FF4A2A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Amazon Brand Registry
                </h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  Register your brand with Amazon for enhanced protection and tools
                </p>
              </div>
            </Link>

            <Link to="/services/ttab-petition" className="group">
              <div className="bg-gradient-to-br from-[#E2F8FE] to-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 h-full">
                <div className="bg-white w-16 h-16 rounded-xl flex items-center justify-center mb-6 shadow-md group-hover:scale-110 transition-transform duration-300">
                  <Lock className="h-8 w-8 text-[#FF4A2A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  TTAB Petition
                </h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  Expert representation for Trademark Trial and Appeal Board proceedings
                </p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Mid-Page CTA Banner - Exact Copy of About Us Section */}
      <section className="relative py-16 bg-gradient-to-r from-[#FF4A2A] via-[#FF6B4A] to-[#FF4A2A] overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-white rounded-full opacity-5 blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-white rounded-full opacity-5 blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Left Side - Content */}
            <div className="text-white">
              <h2 className="text-4xl font-bold mb-4">
                Need Expert Guidance?
              </h2>
              <p className="text-lg opacity-90 mb-6">
                Our team of experienced professionals is here to help you navigate the trademark registration process with confidence and ease.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={onOpenPopup}
                  className="group bg-white text-[#FF4A2A] hover:bg-gray-100 px-8 py-4 rounded-xl font-semibold text-base shadow-xl transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2"
                >
                  Get Free Consultation
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>

            {/* Right Side - Contact Info Cards */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white bg-opacity-10 backdrop-blur-sm border-2 border-white border-opacity-30 rounded-2xl p-6 text-white">
                <div className="bg-white bg-opacity-20 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                </div>
                <h3 className="font-bold text-lg mb-2">Call Us</h3>
                <p className="text-sm opacity-90">+1 (888) 203 1471</p>
                <p className="text-xs opacity-75 mt-2">Mon-Fri 9AM-6PM EST</p>
              </div>
              
              <div className="bg-white bg-opacity-10 backdrop-blur-sm border-2 border-white border-opacity-30 rounded-2xl p-6 text-white">
                <div className="bg-white bg-opacity-20 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="20" height="16" x="2" y="4" rx="2"/>
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                  </svg>
                </div>
                <h3 className="font-bold text-lg mb-2">Email Us</h3>
                <p className="text-sm opacity-90">Info@trustedtrademarkfiler.com</p>
                <p className="text-xs opacity-75 mt-2">24/7 Support Available</p>
              </div>
              
              <div className="col-span-2 bg-white bg-opacity-10 backdrop-blur-sm border-2 border-white border-opacity-30 rounded-2xl p-6 text-center">
                <div className="text-4xl font-bold mb-2">95%</div>
                <p className="text-sm opacity-90">Success Rate</p>
                <p className="text-xs opacity-75 mt-1">Based on 500+ applications</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps - 3 Steps Display */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-[#E2F8FE] rounded-full px-6 py-2 mb-4">
              <span className="text-[#FF4A2A] font-semibold text-base">HOW IT WORKS</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Proven Process
            </h2>
            <p className="text-lg text-gray-600 max-w-xl mx-auto">
              Simple, transparent steps to protect your brand with our streamlined trademark registration process
            </p>
          </div>

          {/* Desktop: Show 3 steps side by side */}
          <div className="hidden md:grid md:grid-cols-3 gap-8">
            {processSteps.slice(0, 3).map((step, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center gap-4 mb-6">
                  <span className="bg-gradient-to-br from-[#FF4A2A] to-[#E63E1F] text-white text-lg font-bold px-6 py-3 rounded-lg shadow-md">
                    {step.step}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {step.title}
                </h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>

          {/* Mobile: Show slider */}
          <div className="md:hidden relative bg-white rounded-xl shadow-lg p-8 border border-gray-100">
            <div className="space-y-6">
              <div className="flex items-center gap-4 mb-4">
                <span className="bg-gradient-to-br from-[#FF4A2A] to-[#E63E1F] text-white text-lg font-bold px-6 py-3 rounded-lg shadow-md">
                  {processSteps[currentProcess].step}
                </span>
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                {processSteps[currentProcess].title}
              </h3>
              <p className="text-base text-gray-700 leading-relaxed">
                {processSteps[currentProcess].description}
              </p>
            </div>
            <div className="flex justify-center gap-2 mt-8">
              {processSteps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentProcess(index)}
                  className={`h-3 rounded-full transition-all duration-300 ${
                    index === currentProcess ? 'w-12 bg-[#FF4A2A]' : 'w-3 bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <PricingSection onOpenPopup={onOpenPopup} />

      {/* Why Choose Us - Enhanced */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -top-8 -left-8 w-64 h-64 bg-[#E2F8FE] rounded-full opacity-50 blur-3xl"></div>
              <img
                src={images.growth}
                alt="Business Growth"
                className="rounded-3xl shadow-2xl w-full h-[400px] object-cover relative z-10 border-4 border-white"
              />
            </div>
            <div className="space-y-5">
              <div className="inline-block bg-[#E2F8FE] rounded-full px-6 py-2">
                <span className="text-[#FF4A2A] font-semibold text-sm">WHY CHOOSE US</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900">
                Why Choose Trusted Trademark Filer?
              </h2>
              <p className="text-base text-gray-600 leading-relaxed">
                We combine legal expertise with modern technology to deliver exceptional trademark protection services with a 95% success rate.
              </p>
              <div className="space-y-3 pt-2">
                <div className="flex items-start gap-3 bg-gradient-to-r from-[#E2F8FE] to-transparent p-4 rounded-xl">
                  <div className="bg-white p-2.5 rounded-lg shadow-md">
                    <TrendingUp className="h-5 w-5 text-[#FF4A2A]" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900 mb-1">
                      High Success Rate
                    </h3>
                    <p className="text-sm text-gray-600">
                      95% approval rate backed by thorough research and expert filing.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-gradient-to-r from-[#E2F8FE] to-transparent p-4 rounded-xl">
                  <div className="bg-white p-2.5 rounded-lg shadow-md">
                    <Shield className="h-5 w-5 text-[#FF4A2A]" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900 mb-1">
                      Expert Guidance
                    </h3>
                    <p className="text-sm text-gray-600">
                      Dedicated case managers and legal experts guide you every step.
                    </p>
                  </div>
                </div>
              </div>
              <Button
                onClick={onOpenPopup}
                className="bg-[#FF4A2A] hover:bg-[#E63E1F] text-white px-8 py-4 text-base font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                Start Your Application <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              What Makes Us Different
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Industry-leading features and benefits that set us apart from the competition
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
              <Clock className="h-12 w-12 text-[#FF4A2A] mb-6" />
              <h3 className="text-lg font-bold text-gray-900 mb-4">Fast Turnaround</h3>
              <p className="text-gray-600 leading-relaxed">
                We process applications quickly and efficiently, typically filing within 24-48 hours of receiving complete information.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
              <Lock className="h-12 w-12 text-[#FF4A2A] mb-6" />
              <h3 className="text-lg font-bold text-gray-900 mb-4">Secure Process</h3>
              <p className="text-gray-600 leading-relaxed">
                Bank-level security for all your documents and information, with encrypted storage and transmission.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
              <Globe className="h-12 w-12 text-[#FF4A2A] mb-6" />
              <h3 className="text-lg font-bold text-gray-900 mb-4">Nationwide Coverage</h3>
              <p className="text-gray-600 leading-relaxed">
                Federal trademark protection across all 50 states, providing comprehensive nationwide rights.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
              <Users className="h-12 w-12 text-[#FF4A2A] mb-6" />
              <h3 className="text-lg font-bold text-gray-900 mb-4">Dedicated Support</h3>
              <p className="text-gray-600 leading-relaxed">
                Personal case manager assigned to your application, available to answer questions and provide updates.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
              <FileText className="h-12 w-12 text-[#FF4A2A] mb-6" />
              <h3 className="text-lg font-bold text-gray-900 mb-4">Transparent Pricing</h3>
              <p className="text-gray-600 leading-relaxed">
                No hidden fees or surprise charges. You'll know exactly what you're paying for upfront.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
              <Star className="h-12 w-12 text-[#FF4A2A] mb-6" />
              <h3 className="text-lg font-bold text-gray-900 mb-4">Proven Track Record</h3>
              <p className="text-gray-600 leading-relaxed">
                95% approval rate with hundreds of successful trademark registrations completed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials - Sliding Display */}
      <section className="py-24 bg-gradient-to-br from-[#E2F8FE] via-white to-[#E2F8FE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-white rounded-full px-6 py-2 mb-4 shadow-md">
              <span className="text-[#FF4A2A] font-semibold text-base">TESTIMONIALS</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-lg text-gray-600">
              Trusted by hundreds of businesses across the USA
            </p>
          </div>

          {/* Desktop: Show 3 testimonials with slider */}
          <div className="hidden md:block relative">
            <div className="grid grid-cols-3 gap-8">
              {testimonials.slice(currentTestimonial, currentTestimonial + 3).map((testimonial, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                  <div className="flex justify-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-[#FF4A2A] fill-[#FF4A2A]" />
                    ))}
                  </div>
                  <p className="text-base text-gray-700 italic leading-relaxed mb-6">
                    "{testimonial.text}"
                  </p>
                  <div className="pt-4 border-t border-gray-200">
                    <p className="font-bold text-gray-900 text-base">
                      {testimonial.name}
                    </p>
                    <p className="text-gray-600 text-sm mt-1">
                      {testimonial.company}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-center gap-2 mt-12">
              {Array.from({ length: Math.ceil(testimonials.length / 3) }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index * 3)}
                  className={`h-3 rounded-full transition-all duration-300 ${
                    Math.floor(currentTestimonial / 3) === index ? 'w-12 bg-[#FF4A2A]' : 'w-3 bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Mobile: Show slider */}
          <div className="md:hidden relative bg-white rounded-xl shadow-lg p-8 border border-gray-100">
            <div className="text-center space-y-6">
              <div className="flex justify-center mb-4">
                {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                  <Star key={i} className="h-6 w-6 text-[#FF4A2A] fill-[#FF4A2A]" />
                ))}
              </div>
              <p className="text-base text-gray-700 italic leading-relaxed">
                "{testimonials[currentTestimonial].text}"
              </p>
              <div className="pt-6 border-t border-gray-200">
                <p className="font-bold text-gray-900 text-base">
                  {testimonials[currentTestimonial].name}
                </p>
                <p className="text-gray-600 text-sm mt-1">
                  {testimonials[currentTestimonial].company}
                </p>
              </div>
            </div>
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`h-3 rounded-full transition-all duration-300 ${
                    index === currentTestimonial ? 'w-12 bg-[#FF4A2A]' : 'w-3 bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section - Creative Design */}
      <section className="relative py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#FF4A2A] rounded-full opacity-10 blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#E2F8FE] rounded-full opacity-10 blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-block bg-[#FF4A2A] bg-opacity-20 border border-[#FF4A2A] rounded-full px-6 py-2 mb-6">
              <span className="text-white font-semibold text-base">START TODAY</span>
            </div>
            <h2 className="text-4xl font-bold mb-6">
              Don't Wait—Protect Your Brand Today
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Your brand is your most valuable asset. Secure it now with our expert trademark services and join hundreds of businesses we've helped protect their intellectual property.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-12">
            <button
              onClick={onOpenPopup}
              className="group bg-[#FF4A2A] hover:bg-[#E63E1F] text-white px-8 py-4 rounded-xl font-semibold text-base shadow-2xl transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2"
            >
              Get Started Now
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => window.openChatWidget && window.openChatWidget()}
              className="bg-white bg-opacity-10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-4 rounded-xl font-semibold text-base transition-all duration-300 hover:scale-105"
            >
              Schedule Consultation
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-6 border border-white border-opacity-20">
                <div className="text-4xl font-bold text-[#FF4A2A] mb-2">95%</div>
                <div className="text-sm text-gray-300">Success Rate</div>
              </div>
            </div>
            <div className="text-center">
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-6 border border-white border-opacity-20">
                <div className="text-4xl font-bold text-[#FF4A2A] mb-2">24-48hr</div>
                <div className="text-sm text-gray-300">Fast Filing</div>
              </div>
            </div>
            <div className="text-center">
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-6 border border-white border-opacity-20">
                <div className="text-4xl font-bold text-[#FF4A2A] mb-2">500+</div>
                <div className="text-sm text-gray-300">Happy Clients</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default HomePage;